/**
 * Building Editor Store - Zustand state management for building editor
 *
 * Manages:
 * - Editor mode (view, edit, add, drag)
 * - Selected building and template
 * - Drag and rotation state
 * - Preview building for new buildings
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { fetchBuildingWithUnit, fetchBuildingWithAllUnits } from '@/lib/api/hangars'
import type { SingleRowParams } from '@/lib/templates/single-row/params'
import { DEFAULT_SINGLE_ROW_VALUES } from '@/lib/templates/single-row/params'
import type { DoubleRowParams } from '@/lib/templates/double-row/params'
import { DEFAULT_DOUBLE_ROW_VALUES, calculateBuildingDepth } from '@/lib/templates/double-row/params'
import type { THangarParams } from '@/lib/templates/standard-t-hangar/params'
import { DEFAULT_T_HANGAR_VALUES } from '@/lib/templates/standard-t-hangar/params'
import { calculateBuildingDepth as calculateTHangarBuildingDepth, calculateRowLength } from '@/lib/templates/standard-t-hangar/calculations'
import type { NestedTHangarParams } from '@/lib/templates/nested-t-hangar/params'
import { DEFAULT_NESTED_T_HANGAR_VALUES } from '@/lib/templates/nested-t-hangar/params'
import { calculateBuildingDepth as calculateNestedBuildingDepth, calculateBuildingWidth as calculateNestedBuildingWidth } from '@/lib/templates/nested-t-hangar/calculations'
import type { ArchHangarParams, ArchProfile, ArchDoorConfig } from '@/lib/templates/arch-hangar/params'
import { DEFAULT_ARCH_HANGAR_VALUES, calculateTotalHeight } from '@/lib/templates/arch-hangar'
import { CoordinateService } from '@/services/3d'
import {
  normalizeFloorColor,
  normalizeWallColor,
  normalizeElevation,
  normalizeBearing,
  type RawBuildingSettings,
} from '@/services/3d/utils/settingsNormalizer'
import type { PricingFormFields } from '@/lib/schemas/pricing-fields'
import type { HangarRow } from '@/types/hangars'
import type { AreaPlacementFieldsInput } from '@/types/area-placement'

/**
 * Extract the event auto-placement fields from a loaded area row, so the
 * Auto-Placement tab can be pre-seeded when editing.
 */
function extractAreaPlacementValues(building: HangarRow): AreaPlacementFieldsInput {
  return {
    area_category: building.area_category,
    turnover_mode: building.turnover_mode,
    parking_mode: building.parking_mode,
    tow_min_wingspan_ft: building.tow_min_wingspan_ft,
    auto_placement: building.auto_placement,
    fill_priority: building.fill_priority,
    pattern: building.pattern,
    active_from: building.active_from,
    active_until: building.active_until,
    release_from: building.release_from,
    release_until: building.release_until,
    placement_heading: building.placement_heading,
    progression: building.progression,
    lanes: building.lanes,
    coupling: building.coupling,
    pitch_mode: building.pitch_mode,
    safety_margin_ft: building.safety_margin_ft,
    auto_sort: building.auto_sort,
    taxiway_exit_point_id: building.taxiway_exit_point_id,
    fallback_area_id: building.fallback_area_id,
    exit_heading: building.exit_heading,
    exit_throughput: building.exit_throughput,
    eligibility: building.eligibility,
    placement_config: building.placement_config,
  }
}

// Re-export SingleRowParams as form values type alias
export type SingleRowFormValues = SingleRowParams

// Re-export DoubleRowParams as form values type alias
export type DoubleRowFormValues = DoubleRowParams

// Re-export THangarParams as form values type alias
export type THangarFormValues = THangarParams

// Re-export NestedTHangarParams as form values type alias
export type NestedTHangarFormValues = NestedTHangarParams

// Re-export ArchHangarParams as form values type alias
export type ArchHangarFormValues = ArchHangarParams

/** Template types supported by the building editor */
export type TemplateType =
  | 'single_unit_community'
  | 'single_row'
  | 'double_row'
  | 'outdoor_rectangular'
  | 'outdoor_row'
  | 'outdoor_grid'
  | 'outdoor_polygon'
  | 'standard_t_hangar'
  | 'nested_t_hangar'
  | 'arch_hangar'

/** Outdoor area subtypes */
export type OutdoorSubtype = 'ramp_area' | 'tiedown_area'

/** Editor mode states */
export type EditorMode = 'view' | 'edit' | 'add' | 'drag'

/** Editor tab within edit mode */
export type EditorTab = 'building' | 'obstacles' | 'subunits'

/** Door wall position (relative to building orientation) */
export type DoorWall = 'front' | 'back' | 'left' | 'right'

/** Door types — re-exported from the template types so all three agree with the DB. */
import type { DoorType } from '@/lib/templates/types'
export type { DoorType }

/** Door specification for a single door opening */
export interface DoorSpec {
  offset_ft: number   // Position along wall from west/south edge
  width_ft: number
  height_ft: number
  type: DoorType
}

/** Doors grouped by wall - supports multiple doors per wall */
export interface DoorsConfig {
  front: DoorSpec[]
  back: DoorSpec[]
  left: DoorSpec[]
  right: DoorSpec[]
}

/** Door configuration for a building (used in PreviewBuilding) */
export interface DoorConfig {
  wall: DoorWall
  width_ft: number
  height_ft: number
  offset_ft: number
  type: DoorType
}

/** Preview building configuration for 3D rendering */
export interface PreviewBuilding {
  id: string
  name: string
  code: string
  width_ft: number
  depth_ft: number
  height_ft: number
  // World coordinates (feet from mapCenter) - PRIMARY for rendering
  worldX: number  // East-West offset in feet (+X = east)
  worldZ: number  // North-South offset in feet (-Z = north)
  elevation_ft: number  // Vertical offset in feet (Y-axis)
  // GPS coordinates - SECONDARY for database storage/geo searches
  latitude: number
  longitude: number
  bearing_deg: number
  floor_color: string
  wall_color: string
  doors: DoorConfig[]
  // Outdoor area visual props (optional)
  border_color?: string
  border_style?: 'solid' | 'dashed'
  border_width_ft?: number
  floor_opacity?: number
}

/** Form values for single unit community template */
export interface SingleUnitFormValues {
  name: string
  code: string
  width_ft: number
  depth_ft: number
  height_ft: number
  elevation_ft: number  // Vertical offset in feet
  // Multi-door support: doors grouped by wall
  doors: DoorsConfig
  floor_color: string
  wall_color: string
  bearing_deg: number
  latitude: number
  longitude: number
}

/** Form values for outdoor rectangular template */
export interface OutdoorRectangularFormValues {
  name: string
  code: string
  width_ft: number
  depth_ft: number
  area_subtype: 'ramp_area' | 'tiedown_area' | 'exit_point' | 'fuel_depot'
  border_color: string
  border_style: 'solid' | 'dashed'
  border_width_ft: number
  floor_color: string | null
  floor_opacity: number
  bearing_deg: number
  latitude: number
  longitude: number
}

/** Helper to create a default door spec */
export function createDefaultDoorSpec(
  wallLength: number,
  doorType: DoorType = 'bifold',
  doorWidth: number = 60
): DoorSpec {
  // offset_ft is the leading edge (left side) of the door
  // To center a door: (wallLength - doorWidth) / 2
  return {
    offset_ft: (wallLength - doorWidth) / 2,
    width_ft: doorWidth,
    height_ft: 20,
    type: doorType,
  }
}

/** Helper to create empty doors config */
export function createEmptyDoorsConfig(): DoorsConfig {
  return {
    front: [],
    back: [],
    left: [],
    right: [],
  }
}

/** Convert DoorsConfig to flat DoorConfig array for PreviewBuilding */
export function doorsConfigToArray(doors: DoorsConfig): DoorConfig[] {
  const result: DoorConfig[] = []
  for (const wall of ['front', 'back', 'left', 'right'] as DoorWall[]) {
    for (const door of doors[wall]) {
      result.push({
        wall,
        offset_ft: door.offset_ft,
        width_ft: door.width_ft,
        height_ft: door.height_ft,
        type: door.type,
      })
    }
  }
  return result
}

/** Convert flat DoorConfig array to DoorsConfig */
export function arrayToDoorsConfig(doors: DoorConfig[]): DoorsConfig {
  const config = createEmptyDoorsConfig()
  for (const door of doors) {
    config[door.wall].push({
      offset_ft: door.offset_ft,
      width_ft: door.width_ft,
      height_ft: door.height_ft,
      type: door.type,
    })
  }
  return config
}

/** Store state interface */
interface BuildingEditorState {
  // Editor mode
  mode: EditorMode
  editorTab: EditorTab

  // Selection state
  selectedBuildingId: string | null
  templateType: TemplateType | null

  // Edit state - IDs of what we're editing (for save)
  editingBuildingId: string | null
  editingUnitId: string | null
  isLoadingBuilding: boolean

  // Interaction state
  isDragging: boolean
  isRotating: boolean
  isPlaced: boolean  // True when building has been placed on map, False = follows cursor
  isDuplicating: boolean  // True when duplicating an existing building
  hoveredBuildingId: string | null

  // Outdoor area pending state (set before template selection)
  pendingOutdoorSubtype: OutdoorSubtype | null

  // Preview building for new buildings
  previewBuilding: PreviewBuilding | null

  // Form values (for current editing session)
  formValues: SingleUnitFormValues | null

  // Single-row form values (separate from single unit form values)
  singleRowFormValues: SingleRowFormValues | null

  // Double-row form values (separate from single unit and single row)
  doubleRowFormValues: DoubleRowFormValues | null

  // T-hangar form values
  tHangarFormValues: THangarFormValues | null

  // Nested T-hangar form values
  nestedTHangarFormValues: NestedTHangarFormValues | null

  // Arch hangar form values
  archHangarFormValues: ArchHangarFormValues | null

  // Outdoor area form values (ramp/tiedown)
  outdoorFormValues: OutdoorRectangularFormValues | null

  // Pricing values loaded from DB when editing existing buildings
  pricingValues: Partial<PricingFormFields> | null

  // Event auto-placement values loaded from DB when editing existing areas
  placementValues: AreaPlacementFieldsInput | null

  // Map center (updated by map component, used for creating preview)
  mapCenter: { lat: number; lng: number } | null

  // Actions
  setMode: (mode: EditorMode) => void
  setEditorTab: (tab: EditorTab) => void
  selectBuilding: (buildingId: string | null) => void
  setTemplateType: (templateType: TemplateType | null) => void
  setDragging: (isDragging: boolean) => void
  setRotating: (isRotating: boolean) => void
  setIsPlaced: (isPlaced: boolean) => void
  setHoveredBuilding: (buildingId: string | null) => void
  setPreviewBuilding: (building: PreviewBuilding | null) => void
  setFormValues: (values: SingleUnitFormValues | null) => void
  updateFormValues: (updates: Partial<SingleUnitFormValues>) => void
  // Single-row form values
  setSingleRowFormValues: (values: SingleRowFormValues | null) => void
  updateSingleRowFormValues: (updates: Partial<SingleRowFormValues>) => void
  // Double-row form values
  setDoubleRowFormValues: (values: DoubleRowFormValues | null) => void
  updateDoubleRowFormValues: (updates: Partial<DoubleRowFormValues>) => void
  // T-hangar form values
  setTHangarFormValues: (values: THangarFormValues | null) => void
  updateTHangarFormValues: (updates: Partial<THangarFormValues>) => void
  // Nested T-hangar form values
  setNestedTHangarFormValues: (values: NestedTHangarFormValues | null) => void
  updateNestedTHangarFormValues: (updates: Partial<NestedTHangarFormValues>) => void
  // Arch hangar form values
  setArchHangarFormValues: (values: ArchHangarFormValues | null) => void
  updateArchHangarFormValues: (updates: Partial<ArchHangarFormValues>) => void
  // Outdoor form values
  setOutdoorFormValues: (values: OutdoorRectangularFormValues | null) => void
  updateOutdoorFormValues: (updates: Partial<OutdoorRectangularFormValues>) => void
  // Event auto-placement values
  setPlacementValues: (values: AreaPlacementFieldsInput | null) => void
  // Position updates - world coordinates are primary, GPS is derived
  updatePreviewWorldPosition: (worldX: number, worldZ: number) => void
  updatePreviewPosition: (lat: number, lng: number) => void  // Legacy GPS update
  updatePreviewRotation: (bearing_deg: number) => void
  setMapCenter: (center: { lat: number; lng: number }) => void

  // Compound actions
  enterAddMode: () => void
  enterOutdoorAddMode: (subtype: OutdoorSubtype) => void
  startAddBuilding: (templateType: TemplateType, centerLat: number, centerLng: number, generatedCode?: string) => void
  cancelAddBuilding: () => void
  startEditBuilding: (buildingId: string) => void
  loadBuildingForEdit: (buildingId: string) => Promise<void>
  loadBuildingForDuplicate: (buildingId: string, newCode: string, newName: string) => Promise<void>
  cancelEdit: () => void
  reset: () => void
}

/** Default building elevation in feet (fixes "flying" buildings) */
export const DEFAULT_ELEVATION_FT = -10

/** Default form values for single unit community template */
export const DEFAULT_SINGLE_UNIT_VALUES: SingleUnitFormValues = {
  name: 'New Hangar',
  code: 'H1',
  width_ft: 100,
  depth_ft: 80,
  height_ft: 24,
  elevation_ft: DEFAULT_ELEVATION_FT,
  doors: {
    front: [{
      // offset_ft is leading edge; to center 60ft door on 100ft wall: (100-60)/2 = 20
      offset_ft: 20,
      width_ft: 60,
      height_ft: 20,
      type: 'bifold',
    }],
    back: [],
    left: [],
    right: [],
  },
  floor_color: '#808080',
  wall_color: '#A0A0A0',
  bearing_deg: 0,
  latitude: 0,
  longitude: 0,
}

// ========================================
// COORDINATE CONVERSION HELPERS
// Convenience aliases for CoordinateService methods
// ========================================

/** Convert GPS to world coordinates (feet from mapCenter) */
const gpsToWorld = CoordinateService.gpsToWorld

/** Convert world coordinates (feet from mapCenter) to GPS */
const worldToGps = CoordinateService.worldToGps

/** Create preview building from form values */
function createPreviewFromFormValues(
  values: SingleUnitFormValues,
  worldX: number,
  worldZ: number,
  mapCenter: { lat: number; lng: number }
): PreviewBuilding {
  // Convert world to GPS for database storage
  const gps = worldToGps(worldX, worldZ, mapCenter)

  return {
    id: `preview-${Date.now()}`,
    name: values.name,
    code: values.code,
    width_ft: values.width_ft,
    depth_ft: values.depth_ft,
    height_ft: values.height_ft,
    worldX,
    worldZ,
    elevation_ft: values.elevation_ft,
    latitude: gps.lat,
    longitude: gps.lng,
    bearing_deg: values.bearing_deg,
    floor_color: values.floor_color,
    wall_color: values.wall_color,
    // Convert DoorsConfig to flat array
    doors: doorsConfigToArray(values.doors),
  }
}

/**
 * Unified form values update handler
 *
 * Handles the common pattern of updating form values and patching the preview building.
 * Used by single-row and double-row templates which share the same update logic.
 *
 * @param updates - Partial form value updates
 * @param currentValues - Current form values (or null)
 * @param preview - Current preview building (or null)
 * @param mapCenter - Map center for GPS conversion
 * @param dimensionsFn - Optional function to calculate updated dimensions
 * @returns Updated form values and preview patches
 */
interface FormValueUpdates {
  latitude?: number
  longitude?: number
  bearing_deg?: number
  [key: string]: unknown
}

interface PreviewPatches {
  worldX?: number
  worldZ?: number
  latitude?: number
  longitude?: number
  bearing_deg?: number
  width_ft?: number
  depth_ft?: number
  height_ft?: number
}

type DimensionsFn<T> = (values: T) => { width_ft: number; depth_ft: number; height_ft: number } | null

function patchPreviewFromUpdates<T extends FormValueUpdates>(
  updates: Partial<T>,
  preview: PreviewBuilding | null,
  mapCenter: { lat: number; lng: number } | null,
  newValues: T,
  dimensionsFn?: DimensionsFn<T>
): PreviewPatches {
  const patches: PreviewPatches = {}

  // Update GPS coordinates ONLY if position actually changed (not just defined)
  // This prevents stale form values from overwriting current position
  if (preview && mapCenter) {
    const latChanged = updates.latitude !== undefined && updates.latitude !== preview.latitude
    const lngChanged = updates.longitude !== undefined && updates.longitude !== preview.longitude

    if (latChanged || lngChanged) {
      const world = gpsToWorld(
        updates.latitude ?? preview.latitude,
        updates.longitude ?? preview.longitude,
        mapCenter
      )
      patches.worldX = world.worldX
      patches.worldZ = world.worldZ
      patches.latitude = updates.latitude ?? preview.latitude
      patches.longitude = updates.longitude ?? preview.longitude
    }
  }

  // Update rotation if bearing changed
  if (updates.bearing_deg !== undefined) {
    patches.bearing_deg = updates.bearing_deg
  }

  // Update dimensions if provided
  if (dimensionsFn) {
    const dims = dimensionsFn(newValues)
    if (dims) {
      patches.width_ft = dims.width_ft
      patches.depth_ft = dims.depth_ft
      patches.height_ft = dims.height_ft
    }
  }

  return patches
}

export const useBuildingEditorStore = create<BuildingEditorState>()(
  devtools(
    (set, get) => ({
      // Initial state
      mode: 'view',
      editorTab: 'building',
      selectedBuildingId: null,
      templateType: null,
      editingBuildingId: null,
      editingUnitId: null,
      isLoadingBuilding: false,
      isDragging: false,
      isRotating: false,
      isPlaced: true,  // Default true for edit mode, set to false when adding new
      isDuplicating: false,
      hoveredBuildingId: null,
      pendingOutdoorSubtype: null,
      previewBuilding: null,
      formValues: null,
      singleRowFormValues: null,
      doubleRowFormValues: null,
      tHangarFormValues: null,
      nestedTHangarFormValues: null,
      archHangarFormValues: null,
      outdoorFormValues: null,
      pricingValues: null,
      placementValues: null,
      mapCenter: null,

      // Basic setters
      setMode: (mode) => set({ mode }),
      setEditorTab: (tab) => set({ editorTab: tab }),

      selectBuilding: (buildingId) => set({ selectedBuildingId: buildingId }),

      setTemplateType: (templateType) => set({ templateType }),

      setDragging: (isDragging) => set({ isDragging }),

      setRotating: (isRotating) => set({ isRotating }),

      setIsPlaced: (isPlaced) => set({ isPlaced }),

      setHoveredBuilding: (buildingId) => set({ hoveredBuildingId: buildingId }),

      setPreviewBuilding: (building) => set({ previewBuilding: building }),

      setFormValues: (values) => set({ formValues: values }),

      updateFormValues: (updates) => {
        const currentValues = get().formValues
        if (!currentValues) return

        const newValues = { ...currentValues, ...updates }
        const preview = get().previewBuilding
        const mapCenter = get().mapCenter

        if (!preview || !mapCenter) {
          set({ formValues: newValues })
          return
        }

        set({
          formValues: newValues,
          // Update preview building if it exists - preserve world position
          previewBuilding: createPreviewFromFormValues(
            newValues,
            preview.worldX,
            preview.worldZ,
            mapCenter
          ),
        })
      },

      // Single-row form value actions
      setSingleRowFormValues: (values) => set({ singleRowFormValues: values }),

      updateSingleRowFormValues: (updates) => {
        const currentValues = get().singleRowFormValues
        if (!currentValues) return

        const newValues = { ...currentValues, ...updates }
        const preview = get().previewBuilding
        const mapCenter = get().mapCenter

        // Update form values and patch preview using unified helper
        set({ singleRowFormValues: newValues })

        if (preview) {
          // IMPORTANT: Exclude lat/lng from patchPreviewFromUpdates to prevent teleporting
          // Position is controlled by drag handler, not form sync. Form lat/lng are stale
          // values from when the form was created - they would overwrite the current cursor position.
          const { latitude, longitude, ...safeUpdates } = updates as Record<string, unknown>
          const patches = patchPreviewFromUpdates(safeUpdates, preview, mapCenter, newValues)
          if (Object.keys(patches).length > 0) {
            set({ previewBuilding: { ...preview, ...patches } })
          }
        }
      },

      // Double-row form value actions
      setDoubleRowFormValues: (values) => set({ doubleRowFormValues: values }),

      updateDoubleRowFormValues: (updates) => {
        const currentValues = get().doubleRowFormValues
        if (!currentValues) return

        const newValues = { ...currentValues, ...updates }
        const preview = get().previewBuilding
        const mapCenter = get().mapCenter

        // Update form values
        set({ doubleRowFormValues: newValues })

        // Patch preview using unified helper with dimension calculation
        if (preview) {
          const dimensionsFn = (values: DoubleRowFormValues) => {
            // Only recalculate if dimension-affecting fields changed
            if (updates.row_a || updates.row_b || updates.width_ft ||
                updates.height_ft || updates.shared_wall_thickness_ft) {
              return {
                width_ft: values.width_ft,
                depth_ft: calculateBuildingDepth(values),
                height_ft: values.height_ft,
              }
            }
            return null
          }

          // IMPORTANT: Exclude lat/lng from patchPreviewFromUpdates to prevent teleporting
          // Position is controlled by drag handler, not form sync. Form lat/lng are stale
          // values from when the form was created - they would overwrite the current cursor position.
          const { latitude, longitude, ...safeUpdates } = updates as Record<string, unknown>
          const patches = patchPreviewFromUpdates(safeUpdates, preview, mapCenter, newValues, dimensionsFn)
          if (Object.keys(patches).length > 0) {
            set({ previewBuilding: { ...preview, ...patches } })
          }
        }
      },

      // T-hangar form value actions
      setTHangarFormValues: (values) => set({ tHangarFormValues: values }),

      updateTHangarFormValues: (updates) => {
        const currentValues = get().tHangarFormValues
        if (!currentValues) return

        const newValues = { ...currentValues, ...updates }
        const preview = get().previewBuilding
        const mapCenter = get().mapCenter

        set({ tHangarFormValues: newValues })

        if (preview) {
          const dimensionsFn = (values: THangarFormValues) => {
            if (updates.unit_width_ft !== undefined || updates.wing_depth_ft !== undefined ||
                updates.tail_width_ft !== undefined || updates.unit_count !== undefined ||
                updates.wall_thickness_ft !== undefined || updates.height_ft !== undefined ||
                updates.default_door_width_ft !== undefined || updates.default_door_height_ft !== undefined) {
              return {
                width_ft: calculateRowLength(values),
                depth_ft: calculateTHangarBuildingDepth(values),
                height_ft: values.height_ft,
              }
            }
            return null
          }

          const { latitude, longitude, ...safeUpdates } = updates as Record<string, unknown>
          const patches = patchPreviewFromUpdates(safeUpdates, preview, mapCenter, newValues, dimensionsFn)
          if (Object.keys(patches).length > 0) {
            set({ previewBuilding: { ...preview, ...patches } })
          }
        }
      },

      // Nested T-hangar form value actions
      setNestedTHangarFormValues: (values) => set({ nestedTHangarFormValues: values }),

      updateNestedTHangarFormValues: (updates) => {
        const currentValues = get().nestedTHangarFormValues
        if (!currentValues) return

        const newValues = { ...currentValues, ...updates }
        const preview = get().previewBuilding
        const mapCenter = get().mapCenter

        set({ nestedTHangarFormValues: newValues })

        if (preview) {
          const dimensionsFn = (values: NestedTHangarFormValues) => {
            if (updates.unit_width_ft !== undefined || updates.wing_depth_ft !== undefined ||
                updates.tail_depth_ft !== undefined || updates.tail_width_ft !== undefined ||
                updates.unit_count !== undefined || updates.wall_thickness_ft !== undefined ||
                updates.height_ft !== undefined || updates.left_end_mode !== undefined || updates.right_end_mode !== undefined) {
              return {
                width_ft: calculateNestedBuildingWidth(values),
                depth_ft: calculateNestedBuildingDepth(values),
                height_ft: values.height_ft,
              }
            }
            return null
          }

          const { latitude, longitude, ...safeUpdates } = updates as Record<string, unknown>
          const patches = patchPreviewFromUpdates(safeUpdates, preview, mapCenter, newValues, dimensionsFn)
          if (Object.keys(patches).length > 0) {
            set({ previewBuilding: { ...preview, ...patches } })
          }
        }
      },

      // Arch hangar form value actions
      setArchHangarFormValues: (values) => set({ archHangarFormValues: values }),

      updateArchHangarFormValues: (updates) => {
        const currentValues = get().archHangarFormValues
        if (!currentValues) return

        const newValues = { ...currentValues, ...updates }
        const preview = get().previewBuilding

        set({ archHangarFormValues: newValues })

        if (preview) {
          const patches: Partial<PreviewBuilding> = {}

          if (updates.span_ft !== undefined) patches.width_ft = newValues.span_ft
          if (updates.length_ft !== undefined) patches.depth_ft = newValues.length_ft
          if (updates.crown_height_ft !== undefined || updates.knee_wall_height_ft !== undefined) {
            patches.height_ft = calculateTotalHeight(newValues.crown_height_ft, newValues.knee_wall_height_ft)
          }
          if (updates.name !== undefined) patches.name = newValues.name
          if (updates.code !== undefined) patches.code = newValues.code
          if (updates.bearing_deg !== undefined) patches.bearing_deg = newValues.bearing_deg
          if (updates.floor_color !== undefined) patches.floor_color = newValues.floor_color
          if (updates.wall_color !== undefined) patches.wall_color = newValues.wall_color

          if (Object.keys(patches).length > 0) {
            set({ previewBuilding: { ...preview, ...patches } })
          }
        }
      },

      // Outdoor form value actions
      setOutdoorFormValues: (values) => set({ outdoorFormValues: values }),
      setPlacementValues: (values) => set({ placementValues: values }),

      updateOutdoorFormValues: (updates) => {
        const currentValues = get().outdoorFormValues
        if (!currentValues) return

        const newValues = { ...currentValues, ...updates }
        const preview = get().previewBuilding
        const mapCenter = get().mapCenter

        set({ outdoorFormValues: newValues })

        if (preview) {
          const { latitude, longitude, ...safeUpdates } = updates as Record<string, unknown>
          const patches = patchPreviewFromUpdates(safeUpdates, preview, mapCenter, newValues)

          // Also patch dimension and visual fields
          if (updates.width_ft !== undefined) patches.width_ft = updates.width_ft
          if (updates.depth_ft !== undefined) patches.depth_ft = updates.depth_ft

          const visualPatches: Partial<PreviewBuilding> = { ...patches }
          if (updates.border_color !== undefined) visualPatches.border_color = updates.border_color
          if (updates.border_style !== undefined) visualPatches.border_style = updates.border_style
          if (updates.border_width_ft !== undefined) visualPatches.border_width_ft = updates.border_width_ft
          if (updates.floor_color !== undefined) visualPatches.floor_color = updates.floor_color ?? '#808080'
          if (updates.floor_opacity !== undefined) visualPatches.floor_opacity = updates.floor_opacity

          if (Object.keys(visualPatches).length > 0) {
            set({ previewBuilding: { ...preview, ...visualPatches } })
          }
        }
      },

      // PRIMARY position update - uses world coordinates directly
      updatePreviewWorldPosition: (worldX, worldZ) => {
        const preview = get().previewBuilding
        const mapCenter = get().mapCenter
        if (!preview || !mapCenter) return

        // Derive GPS from world coordinates
        const gps = worldToGps(worldX, worldZ, mapCenter)

        set({
          previewBuilding: {
            ...preview,
            worldX,
            worldZ,
            latitude: gps.lat,
            longitude: gps.lng,
          },
        })
      },

      // Legacy GPS update - converts to world coordinates
      updatePreviewPosition: (lat, lng) => {
        const preview = get().previewBuilding
        const mapCenter = get().mapCenter
        if (!preview || !mapCenter) return

        // Convert GPS to world coordinates
        const world = gpsToWorld(lat, lng, mapCenter)

        set({
          previewBuilding: {
            ...preview,
            worldX: world.worldX,
            worldZ: world.worldZ,
            latitude: lat,
            longitude: lng,
          },
        })
      },

      updatePreviewRotation: (bearing_deg) => {
        const preview = get().previewBuilding
        if (!preview) return

        set({
          previewBuilding: { ...preview, bearing_deg },
        })

        // Also update form values (single-unit template)
        const formValues = get().formValues
        if (formValues) {
          set({ formValues: { ...formValues, bearing_deg } })
        }

        // Also update single-row form values (multi-unit template)
        const singleRowFormValues = get().singleRowFormValues
        if (singleRowFormValues) {
          set({ singleRowFormValues: { ...singleRowFormValues, bearing_deg } })
        }

        // Also update double-row form values
        const doubleRowFormValues = get().doubleRowFormValues
        if (doubleRowFormValues) {
          set({ doubleRowFormValues: { ...doubleRowFormValues, bearing_deg } })
        }

        // Also update T-hangar form values
        const tHangarFormValues = get().tHangarFormValues
        if (tHangarFormValues) {
          set({ tHangarFormValues: { ...tHangarFormValues, bearing_deg } })
        }

        // Also update outdoor form values
        const outdoorFormValues = get().outdoorFormValues
        if (outdoorFormValues) {
          set({ outdoorFormValues: { ...outdoorFormValues, bearing_deg } })
        }

        // Also update nested T-hangar form values
        const nestedTHangarFormValues = get().nestedTHangarFormValues
        if (nestedTHangarFormValues) {
          set({ nestedTHangarFormValues: { ...nestedTHangarFormValues, bearing_deg } })
        }
        const archHangarFormValues = get().archHangarFormValues
        if (archHangarFormValues) {
          set({ archHangarFormValues: { ...archHangarFormValues, bearing_deg } })
        }
      },

      setMapCenter: (center) => set({ mapCenter: center }),

      // Compound actions
      enterAddMode: () => {
        set({
          mode: 'add',
          templateType: null,
          selectedBuildingId: null,
          previewBuilding: null,
          formValues: null,
          pendingOutdoorSubtype: null,
        })
      },

      enterOutdoorAddMode: (subtype) => {
        set({
          mode: 'add',
          templateType: null,
          selectedBuildingId: null,
          previewBuilding: null,
          formValues: null,
          singleRowFormValues: null,
          doubleRowFormValues: null,
          tHangarFormValues: null,
          nestedTHangarFormValues: null,
          archHangarFormValues: null,
          outdoorFormValues: null,
          pendingOutdoorSubtype: subtype,
        })
      },

      startAddBuilding: (templateType, centerLat, centerLng, generatedCode) => {
        const mapCenter = get().mapCenter || { lat: centerLat, lng: centerLng }

        if (templateType === 'single_row') {
          // Use single-row defaults
          const defaultValues: SingleRowFormValues = {
            ...DEFAULT_SINGLE_ROW_VALUES,
            code: generatedCode || DEFAULT_SINGLE_ROW_VALUES.code,
            latitude: centerLat,
            longitude: centerLng,
          }

          // Create a simple preview building for positioning
          // The actual multi-unit preview is rendered separately
          const preview: PreviewBuilding = {
            id: `preview-${Date.now()}`,
            name: defaultValues.name,
            code: defaultValues.code,
            width_ft: defaultValues.width_ft,
            depth_ft: defaultValues.depth_ft,
            height_ft: defaultValues.height_ft,
            worldX: 0,
            worldZ: 0,
            elevation_ft: DEFAULT_ELEVATION_FT,
            latitude: centerLat,
            longitude: centerLng,
            bearing_deg: defaultValues.bearing_deg,
            floor_color: defaultValues.floor_color,
            wall_color: defaultValues.wall_color,
            doors: [], // Single-row doors are per-unit, not building-level
          }

          set({
            mode: 'add',
            templateType,
            selectedBuildingId: null,
            formValues: null,
            singleRowFormValues: defaultValues,
            doubleRowFormValues: null,
            tHangarFormValues: null,
            nestedTHangarFormValues: null,
            archHangarFormValues: null,
            outdoorFormValues: null,
            previewBuilding: preview,
            isPlaced: false,  // Preview follows cursor until placed
          })
        } else if (templateType === 'double_row') {
          // Use double-row defaults
          const defaultValues: DoubleRowFormValues = {
            ...DEFAULT_DOUBLE_ROW_VALUES,
            code: generatedCode || DEFAULT_DOUBLE_ROW_VALUES.code,
            latitude: centerLat,
            longitude: centerLng,
          }

          // Calculate building depth from row depths + shared wall
          const buildingDepth = calculateBuildingDepth(defaultValues)

          // Create a preview building for positioning
          const preview: PreviewBuilding = {
            id: `preview-${Date.now()}`,
            name: defaultValues.name,
            code: defaultValues.code,
            width_ft: defaultValues.width_ft,
            depth_ft: buildingDepth,
            height_ft: defaultValues.height_ft,
            worldX: 0,
            worldZ: 0,
            elevation_ft: DEFAULT_ELEVATION_FT,
            latitude: centerLat,
            longitude: centerLng,
            bearing_deg: defaultValues.bearing_deg,
            floor_color: defaultValues.floor_color,
            wall_color: defaultValues.wall_color,
            doors: [], // Double-row doors are per-unit, not building-level
          }

          set({
            mode: 'add',
            templateType,
            selectedBuildingId: null,
            formValues: null,
            singleRowFormValues: null,
            doubleRowFormValues: defaultValues,
            tHangarFormValues: null,
            nestedTHangarFormValues: null,
            archHangarFormValues: null,
            outdoorFormValues: null,
            previewBuilding: preview,
            isPlaced: false,  // Preview follows cursor until placed
          })
        } else if (templateType === 'standard_t_hangar') {
          // Use T-hangar defaults
          const defaultValues: THangarFormValues = {
            ...DEFAULT_T_HANGAR_VALUES,
            code: generatedCode || DEFAULT_T_HANGAR_VALUES.code,
            latitude: centerLat,
            longitude: centerLng,
          }

          const rowLength = calculateRowLength(defaultValues)
          const buildingDepthTH = calculateTHangarBuildingDepth(defaultValues)

          const preview: PreviewBuilding = {
            id: `preview-${Date.now()}`,
            name: defaultValues.name,
            code: defaultValues.code,
            width_ft: rowLength,
            depth_ft: buildingDepthTH,
            height_ft: defaultValues.height_ft,
            worldX: 0,
            worldZ: 0,
            elevation_ft: DEFAULT_ELEVATION_FT,
            latitude: centerLat,
            longitude: centerLng,
            bearing_deg: defaultValues.bearing_deg,
            floor_color: defaultValues.floor_color,
            wall_color: defaultValues.wall_color,
            doors: [], // T-hangar doors are per-unit
          }

          set({
            mode: 'add',
            templateType,
            selectedBuildingId: null,
            formValues: null,
            singleRowFormValues: null,
            doubleRowFormValues: null,
            tHangarFormValues: defaultValues,
            nestedTHangarFormValues: null,
            archHangarFormValues: null,
            outdoorFormValues: null,
            previewBuilding: preview,
            isPlaced: false,
          })
        } else if (templateType === 'nested_t_hangar') {
          // Use nested T-hangar defaults
          const defaultValues: NestedTHangarFormValues = {
            ...DEFAULT_NESTED_T_HANGAR_VALUES,
            code: generatedCode || DEFAULT_NESTED_T_HANGAR_VALUES.code,
            latitude: centerLat,
            longitude: centerLng,
          }

          const buildingWidthNested = calculateNestedBuildingWidth(defaultValues)
          const buildingDepthNested = calculateNestedBuildingDepth(defaultValues)

          const preview: PreviewBuilding = {
            id: `preview-${Date.now()}`,
            name: defaultValues.name,
            code: defaultValues.code,
            width_ft: buildingWidthNested,
            depth_ft: buildingDepthNested,
            height_ft: defaultValues.height_ft,
            worldX: 0,
            worldZ: 0,
            elevation_ft: DEFAULT_ELEVATION_FT,
            latitude: centerLat,
            longitude: centerLng,
            bearing_deg: defaultValues.bearing_deg,
            floor_color: defaultValues.floor_color,
            wall_color: defaultValues.wall_color,
            doors: [], // Nested T-hangar doors are per-unit
          }

          set({
            mode: 'add',
            templateType,
            selectedBuildingId: null,
            formValues: null,
            singleRowFormValues: null,
            doubleRowFormValues: null,
            tHangarFormValues: null,
            nestedTHangarFormValues: defaultValues,
            outdoorFormValues: null,
            previewBuilding: preview,
            isPlaced: false,
          })
        } else if (templateType === 'outdoor_rectangular') {
          // Read pending subtype from store (set by enterOutdoorAddMode)
          const subtype = get().pendingOutdoorSubtype || 'ramp_area'
          const isRamp = subtype === 'ramp_area'

          // Use outdoor defaults
          const defaultOutdoorValues: OutdoorRectangularFormValues = {
            name: isRamp ? 'New Ramp Area' : 'New Tiedown Area',
            code: generatedCode || (isRamp ? 'RMP-001' : 'TD-001'),
            width_ft: 200,
            depth_ft: 150,
            area_subtype: subtype,
            border_color: '#FFD700',
            border_style: 'solid',
            border_width_ft: 2,
            floor_color: '#FFD700',
            floor_opacity: 0.3,
            bearing_deg: 0,
            latitude: centerLat,
            longitude: centerLng,
          }

          const preview: PreviewBuilding = {
            id: `preview-${Date.now()}`,
            name: defaultOutdoorValues.name,
            code: defaultOutdoorValues.code,
            width_ft: defaultOutdoorValues.width_ft,
            depth_ft: defaultOutdoorValues.depth_ft,
            height_ft: 0,
            worldX: 0,
            worldZ: 0,
            elevation_ft: 0,
            latitude: centerLat,
            longitude: centerLng,
            bearing_deg: defaultOutdoorValues.bearing_deg,
            floor_color: defaultOutdoorValues.floor_color || '#FFD700',
            wall_color: '#A0A0A0',
            doors: [],
            border_color: defaultOutdoorValues.border_color,
            border_style: defaultOutdoorValues.border_style,
            border_width_ft: defaultOutdoorValues.border_width_ft,
            floor_opacity: defaultOutdoorValues.floor_opacity,
          }

          set({
            mode: 'add',
            templateType,
            selectedBuildingId: null,
            formValues: null,
            singleRowFormValues: null,
            doubleRowFormValues: null,
            tHangarFormValues: null,
            nestedTHangarFormValues: null,
            archHangarFormValues: null,
            outdoorFormValues: defaultOutdoorValues,
            previewBuilding: preview,
            isPlaced: false,
          })
        } else if (templateType === 'arch_hangar') {
          const defaultValues: ArchHangarFormValues = {
            ...DEFAULT_ARCH_HANGAR_VALUES,
            code: generatedCode || DEFAULT_ARCH_HANGAR_VALUES.code,
            latitude: centerLat,
            longitude: centerLng,
          }

          const totalHeight = calculateTotalHeight(defaultValues.crown_height_ft, defaultValues.knee_wall_height_ft)

          const preview: PreviewBuilding = {
            id: `preview-${Date.now()}`,
            name: defaultValues.name,
            code: defaultValues.code,
            width_ft: defaultValues.span_ft,
            depth_ft: defaultValues.length_ft,
            height_ft: totalHeight,
            worldX: 0,
            worldZ: 0,
            elevation_ft: DEFAULT_ELEVATION_FT,
            latitude: centerLat,
            longitude: centerLng,
            bearing_deg: defaultValues.bearing_deg,
            floor_color: defaultValues.floor_color,
            wall_color: defaultValues.wall_color,
            doors: [],
          }

          set({
            mode: 'add',
            templateType,
            selectedBuildingId: null,
            formValues: null,
            singleRowFormValues: null,
            doubleRowFormValues: null,
            tHangarFormValues: null,
            nestedTHangarFormValues: null,
            archHangarFormValues: defaultValues,
            outdoorFormValues: null,
            previewBuilding: preview,
            isPlaced: false,
          })
        } else {
          // Use generated code if provided, otherwise use default
          const defaultValues: SingleUnitFormValues = {
            ...DEFAULT_SINGLE_UNIT_VALUES,
            code: generatedCode || DEFAULT_SINGLE_UNIT_VALUES.code,
          }

          // New building starts at world origin (0, 0) = map center
          const preview = createPreviewFromFormValues(defaultValues, 0, 0, mapCenter)

          set({
            mode: 'add',
            templateType,
            selectedBuildingId: null,
            formValues: defaultValues,
            singleRowFormValues: null,
            doubleRowFormValues: null,
            tHangarFormValues: null,
            nestedTHangarFormValues: null,
            archHangarFormValues: null,
            outdoorFormValues: null,
            previewBuilding: preview,
            isPlaced: false,  // Preview follows cursor until placed
          })
        }
      },

      cancelAddBuilding: () => {
        set({
          mode: 'view',
          editorTab: 'building',
          templateType: null,
          formValues: null,
          singleRowFormValues: null,
          doubleRowFormValues: null,
          tHangarFormValues: null,
          nestedTHangarFormValues: null,
          archHangarFormValues: null,
          outdoorFormValues: null,
          previewBuilding: null,
          isPlaced: true,  // Reset to default
          isDuplicating: false,
          pendingOutdoorSubtype: null,
        })
      },

      startEditBuilding: (buildingId) => {
        // Set mode to edit and trigger loading
        set({
          mode: 'edit',
          selectedBuildingId: buildingId,
          isLoadingBuilding: true,
        })
        // Load the building data asynchronously
        get().loadBuildingForEdit(buildingId)
      },

      loadBuildingForEdit: async (buildingId) => {
        try {
          set({ isLoadingBuilding: true })

          const { building, units: allUnits } = await fetchBuildingWithAllUnits(buildingId)
          const unit = allUnits.length > 0 ? allUnits[0] : null
          const mapCenter = get().mapCenter

          if (!mapCenter) {
            console.error('Map center not set, cannot load building for edit')
            set({ isLoadingBuilding: false })
            return
          }

          // Extract and normalize colors from building settings
          const settings = (building.settings || {}) as RawBuildingSettings
          const floorColor = normalizeFloorColor(settings)
          const wallColor = normalizeWallColor(settings)

          // Normalize bearing to 0-360 degrees
          const bearingDeg = normalizeBearing(building.rotation)

          // Extract door configuration from type_config or build from unit data
          const typeConfig = (building.type_config || {}) as {
            door_positions?: Array<{ side?: string; width?: number; height?: number; offset?: number }>
            doors?: DoorsConfig
          }

          // Try to load doors from type_config first, otherwise build from unit data
          let doors: DoorsConfig
          if (typeConfig.doors) {
            doors = typeConfig.doors
          } else {
            // Build doors from legacy single-door data
            // Map legacy compass directions to relative positions
            const legacySide = typeConfig.door_positions?.[0]?.side
            const sideMapping: Record<string, DoorWall> = {
              'south': 'front', 'north': 'back', 'west': 'left', 'east': 'right'
            }
            const doorWall: DoorWall = legacySide ? (sideMapping[legacySide] || 'front') : 'front'
            const doorWidth = Number(unit?.door_width || building.door_width) || 60
            const doorHeight = Number(unit?.door_height || building.door_height) || 20
            const doorType = (unit?.door_type as DoorType) || 'bifold'
            const length = Number(building.exterior_width) || 100

            doors = createEmptyDoorsConfig()
            doors[doorWall].push({
              // offset_ft is leading edge; to center door: (wallLength - doorWidth) / 2
              offset_ft: (length - doorWidth) / 2,
              width_ft: doorWidth,
              height_ft: doorHeight,
              type: doorType,
            })
          }

          // Normalize elevation from database altitude (stored in meters)
          const elevationFt = normalizeElevation(building.altitude)

          // Build form values from building + unit
          const formValues: SingleUnitFormValues = {
            name: building.name,
            code: building.area_code,
            width_ft: Number(building.exterior_width) || 100,
            depth_ft: Number(building.exterior_depth) || 80,
            height_ft: Number(building.exterior_height) || 30,
            elevation_ft: elevationFt,
            doors,
            floor_color: floorColor,
            wall_color: wallColor,
            bearing_deg: bearingDeg,
            latitude: Number(building.latitude) || 0,
            longitude: Number(building.longitude) || 0,
          }

          // Calculate world coordinates from building GPS
          const world = gpsToWorld(
            Number(building.latitude),
            Number(building.longitude),
            mapCenter
          )

          // Create preview building
          const preview = createPreviewFromFormValues(
            formValues,
            world.worldX,
            world.worldZ,
            mapCenter
          )
          // Use the actual building ID for the preview
          preview.id = building.id

          // Extract pricing values from building row
          const pricingValues: Partial<PricingFormFields> = {
            pricing_model: building.pricing_model ?? 'fixed',
            monthly_rate_private: building.monthly_rate_private ?? undefined,
            monthly_rate_shared: building.monthly_rate_shared ?? undefined,
            transient_rate_nightly: building.transient_rate_nightly ?? undefined,
            rate_per_sqft_private: building.rate_per_sqft_private ?? undefined,
            rate_per_sqft_shared: building.rate_per_sqft_shared ?? undefined,
            construction_year: building.construction_year ?? undefined,
            fire_suppression: building.fire_suppression ?? false,
            security_system: building.security_system ?? false,
          }

          // Extract event auto-placement values from building row (merged into
          // store below; shallow set persists across the per-template set calls)
          set({ placementValues: extractAreaPlacementValues(building) })

          // Determine template type from building's type_config
          // If type_config.template is set, use it; otherwise infer from building.type
          const buildingTypeConfig = building.type_config as {
            template?: string
            unit_count?: number
            wall_thickness_ft?: number
          } | null
          let detectedTemplate = buildingTypeConfig?.template ||
            ((building.type === 'box_hangar_single_row' || building.type === 'box_hangar_double_row') ? 'single_row' : 'single_unit_community')
          // Detect outdoor areas by building type (exit + fuel depot reuse the outdoor flow)
          if (building.type === 'ramp_area' || building.type === 'tiedown_area' || building.type === 'exit_point' || building.type === 'fuel_depot') {
            detectedTemplate = 'outdoor_rectangular'
          }

          // Branch loading based on template type
          if (detectedTemplate === 'single_row') {
            // Load single-row form values from type_config
            const singleRowTypeConfig = buildingTypeConfig as {
              template?: string
              unit_count?: number
              wall_thickness_ft?: number
              default_door_type?: string
              default_door_width_ft?: number
              default_door_height_ft?: number
              unit_overrides?: Array<{ index: number; width_ft?: number; type?: string; doors?: Record<string, Array<{ offset_ft: number; width_ft: number; height_ft: number; type: string }>> }>
            } | null

            const singleRowValues: SingleRowFormValues = {
              name: building.name,
              code: building.area_code,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              width_ft: Number(building.exterior_width) || 200,
              depth_ft: Number(building.exterior_depth) || 50,
              height_ft: Number(building.exterior_height) || 24,
              unit_count: singleRowTypeConfig?.unit_count || 5,
              default_door_type: (singleRowTypeConfig?.default_door_type as DoorType) || (unit?.door_type as DoorType) || 'bifold',
              default_door_width_ft: singleRowTypeConfig?.default_door_width_ft || Number(unit?.door_width) || 36,
              default_door_height_ft: singleRowTypeConfig?.default_door_height_ft || Number(unit?.door_height) || 20,
              unit_overrides: (singleRowTypeConfig?.unit_overrides as SingleRowFormValues['unit_overrides']) || [],
              wall_thickness_ft: singleRowTypeConfig?.wall_thickness_ft || 0.5,
            }

            // Create preview with single-row dimensions
            const singleRowPreview: PreviewBuilding = {
              id: building.id,
              name: singleRowValues.name,
              code: singleRowValues.code,
              width_ft: singleRowValues.width_ft,
              depth_ft: singleRowValues.depth_ft,
              height_ft: singleRowValues.height_ft,
              worldX: world.worldX,
              worldZ: world.worldZ,
              elevation_ft: elevationFt,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              doors: [], // Single-row doors are per-unit, not building-level
            }

            set({
              editingBuildingId: building.id,
              editingUnitId: unit?.id || null,
              formValues: null,
              singleRowFormValues: singleRowValues,
              doubleRowFormValues: null,
              outdoorFormValues: null,
              pricingValues,
              previewBuilding: singleRowPreview,
              templateType: detectedTemplate as TemplateType,
              isLoadingBuilding: false,
            })

            console.log('Loaded single-row building for edit:', {
              buildingId: building.id,
              templateType: detectedTemplate,
              singleRowValues,
            })
          } else if (detectedTemplate === 'double_row') {
            // Load double-row form values from type_config
            const doubleRowTypeConfig = buildingTypeConfig as {
              template?: string
              row_a?: { depth_ft: number; unit_count: number }
              row_b?: { depth_ft: number; unit_count: number }
              shared_wall_thickness_ft?: number
              partition_wall_thickness_ft?: number
              default_door_type?: string
              default_door_width_ft?: number
              default_door_height_ft?: number
              numbering_style?: string
              layout_mode?: string
              row_a_overrides?: Array<{ index: number; width_ft?: number; doors?: Record<string, Array<{ offset_ft: number; width_ft: number; height_ft: number; type: string }>> }>
              row_b_overrides?: Array<{ index: number; width_ft?: number; doors?: Record<string, Array<{ offset_ft: number; width_ft: number; height_ft: number; type: string }>> }>
            } | null

            const doubleRowValues: DoubleRowFormValues = {
              name: building.name,
              code: building.area_code,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              layout_mode: (doubleRowTypeConfig?.layout_mode as 'symmetric' | 'custom') || 'symmetric',
              width_ft: Number(building.exterior_width) || 300,
              height_ft: Number(building.exterior_height) || 26,
              row_a: doubleRowTypeConfig?.row_a || { depth_ft: 90, unit_count: 4 },
              row_b: doubleRowTypeConfig?.row_b || { depth_ft: 90, unit_count: 4 },
              shared_wall_thickness_ft: doubleRowTypeConfig?.shared_wall_thickness_ft || 1.0,
              partition_wall_thickness_ft: doubleRowTypeConfig?.partition_wall_thickness_ft || 0.5,
              default_door_type: (doubleRowTypeConfig?.default_door_type as DoorType) || 'bifold',
              default_door_width_ft: doubleRowTypeConfig?.default_door_width_ft || 60,
              default_door_height_ft: doubleRowTypeConfig?.default_door_height_ft || 22,
              // Load per-unit overrides (contains custom door configurations)
              row_a_overrides: (doubleRowTypeConfig?.row_a_overrides as DoubleRowFormValues['row_a_overrides']) || [],
              row_b_overrides: (doubleRowTypeConfig?.row_b_overrides as DoubleRowFormValues['row_b_overrides']) || [],
              numbering_style: (doubleRowTypeConfig?.numbering_style as 'paired' | 'sequential' | 'custom') || 'paired',
            }

            // Calculate building depth from row depths + shared wall
            const buildingDepth = calculateBuildingDepth(doubleRowValues)

            // Create preview with double-row dimensions
            const doubleRowPreview: PreviewBuilding = {
              id: building.id,
              name: doubleRowValues.name,
              code: doubleRowValues.code,
              width_ft: doubleRowValues.width_ft,
              depth_ft: buildingDepth,
              height_ft: doubleRowValues.height_ft,
              worldX: world.worldX,
              worldZ: world.worldZ,
              elevation_ft: elevationFt,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              doors: [], // Double-row doors are per-unit, not building-level
            }

            set({
              editingBuildingId: building.id,
              editingUnitId: null,  // Double-row has multiple units
              formValues: null,  // Clear single-unit form
              singleRowFormValues: null,  // Clear single-row form
              doubleRowFormValues: doubleRowValues,  // Set double-row form
              pricingValues,
              previewBuilding: doubleRowPreview,
              templateType: detectedTemplate as TemplateType,
              isLoadingBuilding: false,
            })

            console.log('Loaded double-row building for edit:', {
              buildingId: building.id,
              templateType: detectedTemplate,
              doubleRowValues,
            })
          } else if (detectedTemplate === 'standard_t_hangar') {
            // Load standard T-hangar form values from type_config
            const tHangarTypeConfig = buildingTypeConfig as {
              template?: string
              unit_count?: number
              unit_width_ft?: number
              wing_depth_ft?: number
              tail_width_ft?: number
              unit_pitch_ft?: number
              wall_thickness_ft?: number
              default_door_type?: string
              default_door_width_ft?: number
              default_door_height_ft?: number
              unit_overrides?: Array<{ index: number; type?: string; doors?: { front?: Array<{ offset_ft: number; width_ft: number; height_ft: number; type: string }> } }>
            } | null

            const thDefaultDoorType = (tHangarTypeConfig?.default_door_type as DoorType) || (unit?.door_type as DoorType) || 'rolling'
            const thDefaultDoorWidth = tHangarTypeConfig?.default_door_width_ft || Number(unit?.door_width) || 40
            const thDefaultDoorHeight = tHangarTypeConfig?.default_door_height_ft || Number(unit?.door_height) || 12
            const thUnitWidth = tHangarTypeConfig?.unit_width_ft || 44.5

            // Reconstruct overrides from unit records if type_config doesn't have them
            const thUnitOverrides = (tHangarTypeConfig?.unit_overrides as THangarFormValues['unit_overrides']) || []
            if (thUnitOverrides.length === 0 && allUnits.length > 0) {
              for (const u of allUnits) {
                const match = u.unit_code.match(/-(\d+)$/)
                if (!match) continue
                const idx = parseInt(match[1], 10) - 1
                const dw = Number(u.door_width) || thDefaultDoorWidth
                const dh = Number(u.door_height) || thDefaultDoorHeight
                const dt = u.door_type || thDefaultDoorType
                if (Math.abs(dw - thDefaultDoorWidth) > 0.01 || Math.abs(dh - thDefaultDoorHeight) > 0.01 || dt !== thDefaultDoorType) {
                  thUnitOverrides.push({
                    index: idx,
                    doors: {
                      front: [{
                        offset_ft: (thUnitWidth - Math.min(dw, thUnitWidth - 4)) / 2,
                        width_ft: Math.min(dw, thUnitWidth - 4),
                        height_ft: dh,
                        type: dt as DoorType,
                      }],
                    },
                  })
                }
              }
            }

            const tHangarValues: THangarFormValues = {
              name: building.name,
              code: building.area_code,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              unit_width_ft: thUnitWidth,
              wing_depth_ft: tHangarTypeConfig?.wing_depth_ft || 21,
              tail_width_ft: tHangarTypeConfig?.tail_width_ft || 22.5,
              height_ft: Number(building.exterior_height) || 14,
              unit_count: tHangarTypeConfig?.unit_count || 8,
              default_door_type: thDefaultDoorType,
              default_door_width_ft: thDefaultDoorWidth,
              default_door_height_ft: thDefaultDoorHeight,
              wall_thickness_ft: tHangarTypeConfig?.wall_thickness_ft || 0.5,
              unit_overrides: thUnitOverrides,
            }

            const tHangarPreview: PreviewBuilding = {
              id: building.id,
              name: tHangarValues.name,
              code: tHangarValues.code,
              width_ft: calculateRowLength(tHangarValues),
              depth_ft: calculateTHangarBuildingDepth(tHangarValues),
              height_ft: tHangarValues.height_ft,
              worldX: world.worldX,
              worldZ: world.worldZ,
              elevation_ft: elevationFt,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              doors: [],
            }

            set({
              editingBuildingId: building.id,
              editingUnitId: null,
              formValues: null,
              singleRowFormValues: null,
              doubleRowFormValues: null,
              tHangarFormValues: tHangarValues,
              nestedTHangarFormValues: null,
              archHangarFormValues: null,
              outdoorFormValues: null,
              pricingValues,
              previewBuilding: tHangarPreview,
              templateType: detectedTemplate as TemplateType,
              isLoadingBuilding: false,
            })

            console.log('Loaded standard T-hangar building for edit:', {
              buildingId: building.id,
              templateType: detectedTemplate,
              tHangarValues,
            })
          } else if (detectedTemplate === 'nested_t_hangar') {
            // Load nested T-hangar form values from type_config
            const nestedTypeConfig = buildingTypeConfig as {
              template?: string
              unit_count?: number
              row_a_unit_count?: number // backward compat
              unit_width_ft?: number
              wing_depth_ft?: number
              tail_depth_ft?: number
              tail_width_ft?: number
              unit_pitch_ft?: number
              wall_thickness_ft?: number
              end_space_mode?: 'separate' | 'merge_row_a' | 'merge_row_b' // legacy
              left_end_mode?: 'separate' | 'merge_row_a' | 'merge_row_b'
              right_end_mode?: 'separate' | 'merge_row_a' | 'merge_row_b'
              default_door_type?: string
              default_door_width_ft?: number
              default_door_height_ft?: number
              row_a_overrides?: Array<{ index: number; type?: string; doors?: { front?: Array<{ offset_ft: number; width_ft: number; height_ft: number; type: string }> } }>
              row_b_overrides?: Array<{ index: number; type?: string; doors?: { front?: Array<{ offset_ft: number; width_ft: number; height_ft: number; type: string }> } }>
            } | null

            // Handle both new (unit_count) and old (row_a_unit_count) type_config shapes
            const unitCount = nestedTypeConfig?.unit_count
              ?? nestedTypeConfig?.row_a_unit_count
              ?? 10

            const defaultDoorType = (nestedTypeConfig?.default_door_type as DoorType) || (unit?.door_type as DoorType) || 'rolling'
            const defaultDoorWidth = nestedTypeConfig?.default_door_width_ft || Number(unit?.door_width) || 40
            const defaultDoorHeight = nestedTypeConfig?.default_door_height_ft || Number(unit?.door_height) || 12
            const wingDepth = nestedTypeConfig?.wing_depth_ft || 21
            const tailDepth = nestedTypeConfig?.tail_depth_ft || wingDepth

            // Reconstruct overrides from unit records if type_config doesn't have them
            let rowAOverrides = (nestedTypeConfig?.row_a_overrides as NestedTHangarFormValues['row_a_overrides']) || []
            let rowBOverrides = (nestedTypeConfig?.row_b_overrides as NestedTHangarFormValues['row_b_overrides']) || []

            if (rowAOverrides.length === 0 || rowBOverrides.length === 0) {
              // Reconstruct overrides from actual unit records
              const rowAUnits = allUnits.filter(u => u.unit_code.includes('-A-'))
              const rowBUnits = allUnits.filter(u => u.unit_code.includes('-B-'))

              const reconstructOverrides = (
                units: typeof allUnits,
                defWidth: number,
                defHeight: number,
                defType: string
              ) => {
                const overrides: NestedTHangarFormValues['row_a_overrides'] = []
                for (const u of units) {
                  const match = u.unit_code.match(/-(\d+)$/)
                  if (!match) continue
                  const idx = parseInt(match[1], 10) - 1
                  const dw = Number(u.door_width) || defWidth
                  const dh = Number(u.door_height) || defHeight
                  const dt = u.door_type || defType
                  if (Math.abs(dw - defWidth) > 0.01 || Math.abs(dh - defHeight) > 0.01 || dt !== defType) {
                    const unitWidth = nestedTypeConfig?.unit_width_ft || 44.5
                    overrides.push({
                      index: idx,
                      doors: {
                        front: [{
                          offset_ft: (unitWidth - Math.min(dw, unitWidth - 4)) / 2,
                          width_ft: Math.min(dw, unitWidth - 4),
                          height_ft: dh,
                          type: dt as DoorType,
                        }],
                      },
                    })
                  }
                }
                return overrides
              }

              if (rowAOverrides.length === 0) {
                rowAOverrides = reconstructOverrides(rowAUnits, defaultDoorWidth, defaultDoorHeight, defaultDoorType)
              }
              if (rowBOverrides.length === 0) {
                rowBOverrides = reconstructOverrides(rowBUnits, defaultDoorWidth, defaultDoorHeight, defaultDoorType)
              }
            }

            const nestedValues: NestedTHangarFormValues = {
              name: building.name,
              code: building.area_code,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              unit_width_ft: nestedTypeConfig?.unit_width_ft || 44.5,
              wing_depth_ft: wingDepth,
              tail_depth_ft: tailDepth,
              tail_width_ft: nestedTypeConfig?.tail_width_ft || 22.5,
              height_ft: Number(building.exterior_height) || 14,
              unit_count: unitCount,
              default_door_type: defaultDoorType,
              default_door_width_ft: defaultDoorWidth,
              default_door_height_ft: defaultDoorHeight,
              wall_thickness_ft: nestedTypeConfig?.wall_thickness_ft || 0.5,
              left_end_mode: nestedTypeConfig?.left_end_mode || nestedTypeConfig?.end_space_mode || 'separate',
              right_end_mode: nestedTypeConfig?.right_end_mode || nestedTypeConfig?.end_space_mode || 'separate',
              row_a_overrides: rowAOverrides,
              row_b_overrides: rowBOverrides,
            }

            const nestedPreview: PreviewBuilding = {
              id: building.id,
              name: nestedValues.name,
              code: nestedValues.code,
              width_ft: calculateNestedBuildingWidth(nestedValues),
              depth_ft: calculateNestedBuildingDepth(nestedValues),
              height_ft: nestedValues.height_ft,
              worldX: world.worldX,
              worldZ: world.worldZ,
              elevation_ft: elevationFt,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              doors: [],
            }

            set({
              editingBuildingId: building.id,
              editingUnitId: null,
              formValues: null,
              singleRowFormValues: null,
              doubleRowFormValues: null,
              tHangarFormValues: null,
              nestedTHangarFormValues: nestedValues,
              archHangarFormValues: null,
              outdoorFormValues: null,
              pricingValues,
              previewBuilding: nestedPreview,
              templateType: detectedTemplate as TemplateType,
              isLoadingBuilding: false,
            })

            console.log('Loaded nested T-hangar building for edit:', {
              buildingId: building.id,
              templateType: detectedTemplate,
              nestedValues,
            })
          } else if (detectedTemplate === 'arch_hangar') {
            // Load arch hangar form values from type_config
            const archTypeConfig = buildingTypeConfig as {
              template?: string
              span_ft?: number
              length_ft?: number
              crown_height_ft?: number
              knee_wall_height_ft?: number
              arch_profile?: ArchProfile
              arch_segments?: number
              wall_thickness_ft?: number
              front_door?: ArchDoorConfig
              back_door?: ArchDoorConfig
            } | null

            const archValues: ArchHangarFormValues = {
              name: building.name,
              code: building.area_code,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              span_ft: archTypeConfig?.span_ft || Number(building.exterior_width) || 80,
              length_ft: archTypeConfig?.length_ft || Number(building.exterior_depth) || 100,
              crown_height_ft: archTypeConfig?.crown_height_ft || 40,
              knee_wall_height_ft: archTypeConfig?.knee_wall_height_ft || 0,
              arch_profile: archTypeConfig?.arch_profile || 'semicircular',
              arch_segments: archTypeConfig?.arch_segments || 24,
              wall_thickness_ft: archTypeConfig?.wall_thickness_ft || 0.5,
              front_door: archTypeConfig?.front_door || DEFAULT_ARCH_HANGAR_VALUES.front_door,
              back_door: archTypeConfig?.back_door || DEFAULT_ARCH_HANGAR_VALUES.back_door,
            }

            const archTotalHeight = calculateTotalHeight(archValues.crown_height_ft, archValues.knee_wall_height_ft)

            const archPreview: PreviewBuilding = {
              id: building.id,
              name: archValues.name,
              code: archValues.code,
              width_ft: archValues.span_ft,
              depth_ft: archValues.length_ft,
              height_ft: archTotalHeight,
              worldX: world.worldX,
              worldZ: world.worldZ,
              elevation_ft: elevationFt,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              doors: [],
            }

            set({
              editingBuildingId: building.id,
              editingUnitId: null,
              formValues: null,
              singleRowFormValues: null,
              doubleRowFormValues: null,
              tHangarFormValues: null,
              nestedTHangarFormValues: null,
              archHangarFormValues: archValues,
              outdoorFormValues: null,
              pricingValues,
              previewBuilding: archPreview,
              templateType: detectedTemplate as TemplateType,
              isLoadingBuilding: false,
            })

            console.log('Loaded arch hangar building for edit:', {
              buildingId: building.id,
              templateType: detectedTemplate,
              archValues,
            })
          } else if (detectedTemplate === 'outdoor_rectangular') {
            // Load outdoor area form values from building settings
            const outdoorValues: OutdoorRectangularFormValues = {
              name: building.name,
              code: building.area_code,
              width_ft: Number(building.exterior_width) || 200,
              depth_ft: Number(building.exterior_depth) || 150,
              area_subtype: building.type as 'ramp_area' | 'tiedown_area' | 'exit_point' | 'fuel_depot',
              border_color: (settings as Record<string, unknown>).border_color as string || '#FFD700',
              border_style: ((settings as Record<string, unknown>).border_style as 'solid' | 'dashed') || 'solid',
              border_width_ft: ((settings as Record<string, unknown>).border_width_ft as number) ?? 2,
              floor_color: floorColor || null,
              floor_opacity: ((settings as Record<string, unknown>).floor_opacity as number) ?? 0.3,
              bearing_deg: bearingDeg,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
            }

            const outdoorPreview: PreviewBuilding = {
              id: building.id,
              name: outdoorValues.name,
              code: outdoorValues.code,
              width_ft: outdoorValues.width_ft,
              depth_ft: outdoorValues.depth_ft,
              height_ft: 0,
              worldX: world.worldX,
              worldZ: world.worldZ,
              elevation_ft: 0,
              latitude: Number(building.latitude),
              longitude: Number(building.longitude),
              bearing_deg: bearingDeg,
              floor_color: outdoorValues.floor_color || '#FFD700',
              wall_color: wallColor,
              doors: [],
              border_color: outdoorValues.border_color,
              border_style: outdoorValues.border_style,
              border_width_ft: outdoorValues.border_width_ft,
              floor_opacity: outdoorValues.floor_opacity,
            }

            set({
              editingBuildingId: building.id,
              editingUnitId: unit?.id || null,
              formValues: null,
              singleRowFormValues: null,
              doubleRowFormValues: null,
              tHangarFormValues: null,
              nestedTHangarFormValues: null,
              archHangarFormValues: null,
              outdoorFormValues: outdoorValues,
              pricingValues,
              previewBuilding: outdoorPreview,
              templateType: detectedTemplate as TemplateType,
              isLoadingBuilding: false,
            })

            console.log('Loaded outdoor area for edit:', {
              buildingId: building.id,
              templateType: detectedTemplate,
              outdoorValues,
            })
          } else {
            // Load single-unit form values (existing logic)
            set({
              editingBuildingId: building.id,
              editingUnitId: unit?.id || null,
              formValues,  // Set single-unit form
              singleRowFormValues: null,
              doubleRowFormValues: null,
              tHangarFormValues: null,
              nestedTHangarFormValues: null,
              archHangarFormValues: null,
              outdoorFormValues: null,
              pricingValues,
              previewBuilding: preview,
              templateType: detectedTemplate as TemplateType,
              isLoadingBuilding: false,
            })

            console.log('Loaded single-unit building for edit:', {
              buildingId: building.id,
              unitId: unit?.id,
              templateType: detectedTemplate,
              formValues,
            })
          }
        } catch (error) {
          console.error('Failed to load building for edit:', error)
          set({ isLoadingBuilding: false })
        }
      },

      loadBuildingForDuplicate: async (buildingId, newCode, newName) => {
        try {
          set({ isLoadingBuilding: true })

          const { building, unit } = await fetchBuildingWithUnit(buildingId)
          const mapCenter = get().mapCenter

          if (!mapCenter) {
            console.error('Map center not set, cannot duplicate building')
            set({ isLoadingBuilding: false })
            return
          }

          // Extract and normalize colors from building settings
          const settings = (building.settings || {}) as RawBuildingSettings
          const floorColor = normalizeFloorColor(settings)
          const wallColor = normalizeWallColor(settings)

          // Normalize bearing to 0-360 degrees
          const bearingDeg = normalizeBearing(building.rotation)

          // Normalize elevation from database altitude (stored in meters)
          const elevationFt = normalizeElevation(building.altitude)

          // Extract door configuration from type_config or build from unit data
          const typeConfig = (building.type_config || {}) as {
            door_positions?: Array<{ side?: string; width?: number; height?: number; offset?: number }>
            doors?: DoorsConfig
          }

          let doors: DoorsConfig
          if (typeConfig.doors) {
            doors = typeConfig.doors
          } else {
            const legacySide = typeConfig.door_positions?.[0]?.side
            const sideMapping: Record<string, DoorWall> = {
              'south': 'front', 'north': 'back', 'west': 'left', 'east': 'right'
            }
            const doorWall: DoorWall = legacySide ? (sideMapping[legacySide] || 'front') : 'front'
            const doorWidth = Number(unit?.door_width || building.door_width) || 60
            const doorHeight = Number(unit?.door_height || building.door_height) || 20
            const doorType = (unit?.door_type as DoorType) || 'bifold'
            const length = Number(building.exterior_width) || 100

            doors = createEmptyDoorsConfig()
            doors[doorWall].push({
              offset_ft: (length - doorWidth) / 2,
              width_ft: doorWidth,
              height_ft: doorHeight,
              type: doorType,
            })
          }

          // Extract pricing values from building row
          const pricingValues: Partial<PricingFormFields> = {
            pricing_model: building.pricing_model ?? 'fixed',
            monthly_rate_private: building.monthly_rate_private ?? undefined,
            monthly_rate_shared: building.monthly_rate_shared ?? undefined,
            transient_rate_nightly: building.transient_rate_nightly ?? undefined,
            rate_per_sqft_private: building.rate_per_sqft_private ?? undefined,
            rate_per_sqft_shared: building.rate_per_sqft_shared ?? undefined,
            construction_year: building.construction_year ?? undefined,
            fire_suppression: building.fire_suppression ?? false,
            security_system: building.security_system ?? false,
          }

          // Extract event auto-placement values from building row (merged into
          // store below; shallow set persists across the per-template set calls)
          set({ placementValues: extractAreaPlacementValues(building) })

          // Determine template type from building's type_config
          const buildingTypeConfig = building.type_config as {
            template?: string
            unit_count?: number
            wall_thickness_ft?: number
          } | null
          const isOutdoor = building.type === 'ramp_area' || building.type === 'tiedown_area' || building.type === 'exit_point' || building.type === 'fuel_depot'
          const detectedTemplate = isOutdoor ? 'outdoor_rectangular'
            : (buildingTypeConfig?.template ||
            ((building.type === 'box_hangar_single_row' || building.type === 'box_hangar_double_row') ? 'single_row' : 'single_unit_community'))

          const previewId = `preview-dup-${Date.now()}`

          // Branch loading based on template type
          if (detectedTemplate === 'single_row') {
            const singleRowValues: SingleRowFormValues = {
              name: newName,
              code: newCode,
              latitude: mapCenter.lat,
              longitude: mapCenter.lng,
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              width_ft: Number(building.exterior_width) || 200,
              depth_ft: Number(building.exterior_depth) || 50,
              height_ft: Number(building.exterior_height) || 24,
              unit_count: buildingTypeConfig?.unit_count || 5,
              default_door_type: 'bifold',
              default_door_width_ft: 36,
              default_door_height_ft: 20,
              unit_overrides: [],
              wall_thickness_ft: buildingTypeConfig?.wall_thickness_ft || 0.5,
            }

            const singleRowPreview: PreviewBuilding = {
              id: previewId,
              name: newName,
              code: newCode,
              width_ft: singleRowValues.width_ft,
              depth_ft: singleRowValues.depth_ft,
              height_ft: singleRowValues.height_ft,
              worldX: 0,
              worldZ: 0,
              elevation_ft: elevationFt,
              latitude: mapCenter.lat,
              longitude: mapCenter.lng,
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              doors: [],
            }

            set({
              mode: 'add',
              editingBuildingId: null,
              editingUnitId: null,
              formValues: null,
              singleRowFormValues: singleRowValues,
              doubleRowFormValues: null,
              tHangarFormValues: null,
              nestedTHangarFormValues: null,
              archHangarFormValues: null,
              outdoorFormValues: null,
              pricingValues,
              previewBuilding: singleRowPreview,
              templateType: detectedTemplate as TemplateType,
              isPlaced: false,
              isDuplicating: true,
              isLoadingBuilding: false,
            })
          } else if (detectedTemplate === 'double_row') {
            const doubleRowTypeConfig = buildingTypeConfig as {
              template?: string
              row_a?: { depth_ft: number; unit_count: number }
              row_b?: { depth_ft: number; unit_count: number }
              shared_wall_thickness_ft?: number
              partition_wall_thickness_ft?: number
              default_door_type?: string
              default_door_width_ft?: number
              default_door_height_ft?: number
              numbering_style?: string
              layout_mode?: string
              row_a_overrides?: Array<{ index: number; width_ft?: number; doors?: Record<string, Array<{ offset_ft: number; width_ft: number; height_ft: number; type: string }>> }>
              row_b_overrides?: Array<{ index: number; width_ft?: number; doors?: Record<string, Array<{ offset_ft: number; width_ft: number; height_ft: number; type: string }>> }>
            } | null

            const doubleRowValues: DoubleRowFormValues = {
              name: newName,
              code: newCode,
              latitude: mapCenter.lat,
              longitude: mapCenter.lng,
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              layout_mode: (doubleRowTypeConfig?.layout_mode as 'symmetric' | 'custom') || 'symmetric',
              width_ft: Number(building.exterior_width) || 300,
              height_ft: Number(building.exterior_height) || 26,
              row_a: doubleRowTypeConfig?.row_a || { depth_ft: 90, unit_count: 4 },
              row_b: doubleRowTypeConfig?.row_b || { depth_ft: 90, unit_count: 4 },
              shared_wall_thickness_ft: doubleRowTypeConfig?.shared_wall_thickness_ft || 1.0,
              partition_wall_thickness_ft: doubleRowTypeConfig?.partition_wall_thickness_ft || 0.5,
              default_door_type: (doubleRowTypeConfig?.default_door_type as DoorType) || 'bifold',
              default_door_width_ft: doubleRowTypeConfig?.default_door_width_ft || 60,
              default_door_height_ft: doubleRowTypeConfig?.default_door_height_ft || 22,
              row_a_overrides: (doubleRowTypeConfig?.row_a_overrides as DoubleRowFormValues['row_a_overrides']) || [],
              row_b_overrides: (doubleRowTypeConfig?.row_b_overrides as DoubleRowFormValues['row_b_overrides']) || [],
              numbering_style: (doubleRowTypeConfig?.numbering_style as 'paired' | 'sequential' | 'custom') || 'paired',
            }

            const buildingDepth = calculateBuildingDepth(doubleRowValues)

            const doubleRowPreview: PreviewBuilding = {
              id: previewId,
              name: newName,
              code: newCode,
              width_ft: doubleRowValues.width_ft,
              depth_ft: buildingDepth,
              height_ft: doubleRowValues.height_ft,
              worldX: 0,
              worldZ: 0,
              elevation_ft: elevationFt,
              latitude: mapCenter.lat,
              longitude: mapCenter.lng,
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              doors: [],
            }

            set({
              mode: 'add',
              editingBuildingId: null,
              editingUnitId: null,
              formValues: null,
              singleRowFormValues: null,
              doubleRowFormValues: doubleRowValues,
              tHangarFormValues: null,
              nestedTHangarFormValues: null,
              archHangarFormValues: null,
              outdoorFormValues: null,
              pricingValues,
              previewBuilding: doubleRowPreview,
              templateType: detectedTemplate as TemplateType,
              isPlaced: false,
              isDuplicating: true,
              isLoadingBuilding: false,
            })
          } else if (detectedTemplate === 'arch_hangar') {
            const archTypeConfig = buildingTypeConfig as {
              template?: string
              span_ft?: number
              length_ft?: number
              crown_height_ft?: number
              knee_wall_height_ft?: number
              arch_profile?: ArchProfile
              arch_segments?: number
              wall_thickness_ft?: number
              front_door?: ArchDoorConfig
              back_door?: ArchDoorConfig
            } | null

            const archValues: ArchHangarFormValues = {
              name: newName,
              code: newCode,
              latitude: mapCenter.lat,
              longitude: mapCenter.lng,
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              span_ft: archTypeConfig?.span_ft || Number(building.exterior_width) || 80,
              length_ft: archTypeConfig?.length_ft || Number(building.exterior_depth) || 100,
              crown_height_ft: archTypeConfig?.crown_height_ft || 40,
              knee_wall_height_ft: archTypeConfig?.knee_wall_height_ft || 0,
              arch_profile: archTypeConfig?.arch_profile || 'semicircular',
              arch_segments: archTypeConfig?.arch_segments || 24,
              wall_thickness_ft: archTypeConfig?.wall_thickness_ft || 0.5,
              front_door: archTypeConfig?.front_door || DEFAULT_ARCH_HANGAR_VALUES.front_door,
              back_door: archTypeConfig?.back_door || DEFAULT_ARCH_HANGAR_VALUES.back_door,
            }

            const archTotalHeight = calculateTotalHeight(archValues.crown_height_ft, archValues.knee_wall_height_ft)

            const archPreview: PreviewBuilding = {
              id: previewId,
              name: newName,
              code: newCode,
              width_ft: archValues.span_ft,
              depth_ft: archValues.length_ft,
              height_ft: archTotalHeight,
              worldX: 0,
              worldZ: 0,
              elevation_ft: elevationFt,
              latitude: mapCenter.lat,
              longitude: mapCenter.lng,
              bearing_deg: bearingDeg,
              floor_color: floorColor,
              wall_color: wallColor,
              doors: [],
            }

            set({
              mode: 'add',
              editingBuildingId: null,
              editingUnitId: null,
              formValues: null,
              singleRowFormValues: null,
              doubleRowFormValues: null,
              tHangarFormValues: null,
              nestedTHangarFormValues: null,
              archHangarFormValues: archValues,
              outdoorFormValues: null,
              pricingValues,
              previewBuilding: archPreview,
              templateType: detectedTemplate as TemplateType,
              isPlaced: false,
              isDuplicating: true,
              isLoadingBuilding: false,
            })
          } else if (detectedTemplate === 'outdoor_rectangular') {
            const areaSubtype =
              building.type === 'exit_point'
                ? 'exit_point'
                : building.type === 'fuel_depot'
                  ? 'fuel_depot'
                  : building.type === 'tiedown_area'
                    ? 'tiedown_area'
                    : 'ramp_area'
            const outdoorValues: OutdoorRectangularFormValues = {
              name: newName,
              code: newCode,
              area_subtype: areaSubtype,
              width_ft: Number(building.exterior_width) || 200,
              depth_ft: Number(building.exterior_depth) || 150,
              border_color: settings.border_color || settings.borderColor || '#FFD700',
              border_style: (settings.border_style || settings.borderStyle || 'solid') as 'solid' | 'dashed',
              border_width_ft: settings.border_width_ft ?? settings.borderWidthFt ?? 2,
              floor_color: floorColor === '#808080' ? null : floorColor,
              floor_opacity: settings.floor_opacity ?? settings.floorOpacity ?? 0.3,
              bearing_deg: bearingDeg,
              latitude: mapCenter.lat,
              longitude: mapCenter.lng,
            }

            const outdoorPreview: PreviewBuilding = {
              id: previewId,
              name: newName,
              code: newCode,
              width_ft: outdoorValues.width_ft,
              depth_ft: outdoorValues.depth_ft,
              height_ft: 0,
              worldX: 0,
              worldZ: 0,
              elevation_ft: 0,
              latitude: mapCenter.lat,
              longitude: mapCenter.lng,
              bearing_deg: bearingDeg,
              floor_color: outdoorValues.floor_color || '#FFD700',
              wall_color: '#FFD700',
              doors: [],
              border_color: outdoorValues.border_color,
              border_style: outdoorValues.border_style,
              border_width_ft: outdoorValues.border_width_ft,
              floor_opacity: outdoorValues.floor_opacity,
            }

            set({
              mode: 'add',
              editingBuildingId: null,
              editingUnitId: null,
              formValues: null,
              singleRowFormValues: null,
              doubleRowFormValues: null,
              tHangarFormValues: null,
              nestedTHangarFormValues: null,
              archHangarFormValues: null,
              outdoorFormValues: outdoorValues,
              pricingValues,
              previewBuilding: outdoorPreview,
              templateType: detectedTemplate as TemplateType,
              isPlaced: false,
              isDuplicating: true,
              isLoadingBuilding: false,
            })
          } else {
            // Single-unit community
            const formValues: SingleUnitFormValues = {
              name: newName,
              code: newCode,
              width_ft: Number(building.exterior_width) || 100,
              depth_ft: Number(building.exterior_depth) || 80,
              height_ft: Number(building.exterior_height) || 30,
              elevation_ft: elevationFt,
              doors,
              floor_color: floorColor,
              wall_color: wallColor,
              bearing_deg: bearingDeg,
              latitude: mapCenter.lat,
              longitude: mapCenter.lng,
            }

            const preview = createPreviewFromFormValues(formValues, 0, 0, mapCenter)
            preview.id = previewId

            set({
              mode: 'add',
              editingBuildingId: null,
              editingUnitId: null,
              formValues,
              singleRowFormValues: null,
              doubleRowFormValues: null,
              tHangarFormValues: null,
              nestedTHangarFormValues: null,
              archHangarFormValues: null,
              outdoorFormValues: null,
              pricingValues,
              previewBuilding: preview,
              templateType: detectedTemplate as TemplateType,
              isPlaced: false,
              isDuplicating: true,
              isLoadingBuilding: false,
            })
          }
        } catch (error) {
          console.error('Failed to load building for duplicate:', error)
          set({ isLoadingBuilding: false })
        }
      },

      cancelEdit: () => {
        set({
          mode: 'view',
          editorTab: 'building',
          selectedBuildingId: null,
          editingBuildingId: null,
          editingUnitId: null,
          formValues: null,
          singleRowFormValues: null,
          doubleRowFormValues: null,
          outdoorFormValues: null,
          pricingValues: null,
          placementValues: null,
          previewBuilding: null,
          pendingOutdoorSubtype: null,
        })
      },

      reset: () => {
        set({
          mode: 'view',
          editorTab: 'building',
          selectedBuildingId: null,
          templateType: null,
          editingBuildingId: null,
          editingUnitId: null,
          isLoadingBuilding: false,
          isDragging: false,
          isRotating: false,
          isDuplicating: false,
          hoveredBuildingId: null,
          pendingOutdoorSubtype: null,
          previewBuilding: null,
          formValues: null,
          singleRowFormValues: null,
          doubleRowFormValues: null,
          tHangarFormValues: null,
          nestedTHangarFormValues: null,
          archHangarFormValues: null,
          outdoorFormValues: null,
          pricingValues: null,
          placementValues: null,
          isPlaced: true,  // Reset to default
        })
      },
    }),
    { name: 'building-editor-store' }
  )
)
